import { copyPairs, worldSources } from '../data/world/sources'
import { eventEquivalences } from '../data/world/eventEquivalences'
import { excludedItemReason, isExcludedItemName } from '../data/world/excludedItems'
import { variantDisplayName } from '../data/guide/variantDisplayNames'
import type { ExcludedItem } from '../types/world'
import type { SourceKey } from '../data/world/sources'
import { bool, fetchCsv, num, parseJson, str } from './csv'
import type { Row } from './csv'
import { parseQuests } from './questLoader'
import { parseServerFlags } from './eventStatus'
import type {
  Bag, BagReward, BagRewardPool, BagRewardVariant, BagSection, BagTrigger, CopyComparison, DropRoute, DropRule, EventBag, EventDirectItem,
  EventLink, EventMapChoice, EventRoom, EventSchedule, FileReport, GameEvent, Item, ItemVariant, Monster, MonsterLocation,
  RouteTarget, RuleTarget, ServerRate, Spawn, WorldData, WorldMap,
} from '../types/world'

function groupBy<T, K>(list: T[], key: (t: T) => K | undefined): Map<K, T[]> {
  const m = new Map<K, T[]>()
  list.forEach((t) => {
    const k = key(t)
    if (k === undefined) return
    const arr = m.get(k)
    if (arr) arr.push(t)
    else m.set(k, [t])
  })
  return m
}

const numArray = (v: unknown): number[] => (Array.isArray(v) ? v.filter((x): x is number => typeof x === 'number') : [])

export async function loadWorld(): Promise<WorldData> {
  const keys = Object.keys(worldSources) as SourceKey[]
  const settled = await Promise.allSettled(keys.map((k) => fetchCsv(worldSources[k].url)))

  const rows = {} as Record<SourceKey, Row[]>
  const reports = {} as Record<SourceKey, FileReport>
  settled.forEach((r, i) => {
    const k = keys[i]
    const src = worldSources[k]
    const rep: FileReport = { key: k, label: src.label, expected: src.expected, rows: 0, loaded: r.status === 'fulfilled', parseErrors: [], duplicateIds: [], duplicateCount: 0, emptyIds: 0 }
    if (r.status === 'fulfilled') {
      rows[k] = r.value.rows
      rep.rows = r.value.rows.length
      rep.parseErrors.push(...r.value.errors)
    } else {
      rows[k] = []
      rep.parseErrors.push(`No se pudo descargar: ${r.reason instanceof Error ? r.reason.message : String(r.reason)}`)
    }
    reports[k] = rep
  })

  const accept = <K extends string | number>(key: SourceKey, id: K | undefined, seen: Set<K>): id is K => {
    const rep = reports[key]
    if (id === undefined) {
      rep.emptyIds++
      return false
    }
    if (seen.has(id)) {
      rep.duplicateCount++
      if (rep.duplicateIds.length < 50) rep.duplicateIds.push(String(id))
      return false
    }
    seen.add(id)
    return true
  }

  const jsonOf = <T,>(key: SourceKey, rowId: string | undefined, col: string, v: string | undefined): T | undefined => {
    const s = str(v)
    if (s === undefined) return undefined
    const parsed = parseJson<T>(s)
    if (parsed === undefined && reports[key].parseErrors.length < 80) reports[key].parseErrors.push(`${rowId ?? '?'}: la columna ${col} no es JSON válido`)
    return parsed
  }

  // ── Mapas ──
  const maps = new Map<number, WorldMap>()
  const seenMaps = new Set<number>()
  rows.maps.forEach((r) => {
    const id = num(r.id)
    if (!accept('maps', id, seenMaps)) return
    const entries = jsonOf<unknown>('maps', r.id, 'move_entries', r.move_entries)
    const notes = jsonOf<unknown>('maps', r.id, 'notes', r.notes)
    maps.set(id, {
      id,
      name: str(r.name) ?? `Mapa ${id}`,
      minimumMoveLevel: num(r.minimum_move_level),
      moveEntries: Array.isArray(entries)
        ? entries.map((e: { name?: unknown; minLevel?: unknown; gateId?: unknown }) => ({
            name: String(e?.name ?? ''),
            minLevel: typeof e?.minLevel === 'number' ? e.minLevel : undefined,
            gateId: typeof e?.gateId === 'number' ? e.gateId : undefined,
          }))
        : [],
      notes: Array.isArray(notes) ? notes.map(String) : [],
      terrainImage: str(r.terrain_image),
      axisHorizontal: str(r.coordinate_horizontal),
      axisVertical: str(r.coordinate_vertical),
      experienceRate: num(r.experience_rate),
      itemDropRate: num(r.item_drop_rate),
      excellentRatePerMillion: num(r.excellent_rate_per_million),
      ancientRatePerMillion: num(r.ancient_rate_per_million),
      socketItemsAllowed: bool(r.socket_items_allowed),
    })
  })

  // ── Monstruos ──
  const monsters = new Map<number, Monster>()
  const seenMonsters = new Set<number>()
  rows.monsters.forEach((r) => {
    const id = num(r.id)
    if (!accept('monsters', id, seenMonsters)) return
    monsters.set(id, {
      id,
      name: str(r.name) ?? `Monstruo ${id}`,
      level: num(r.level_catalog),
      hp: num(r.hp_catalog),
      damageMin: num(r.damage_min_catalog),
      damageMax: num(r.damage_max_catalog),
      defense: num(r.defense_catalog),
      respawnSeconds: num(r.respawn_seconds_catalog),
      itemRateDivisor: num(r.item_rate_divisor),
    })
  })

  // El paquete del 08/10/2026 trae una sola copia de cada tabla: ya no hay copias que comparar.
  const sig = (r: Row) => JSON.stringify(Object.keys(r).sort().map((k) => [k, r[k]]))
  const monstersCopy = { rows: 0, differentRows: 0, identical: true }

  // Comparación genérica de copias subidas de nuevo (fila por fila, por id).
  const copies: CopyComparison[] = copyPairs.map(({ copy, original: orig }) => {
    const origMap = new Map(rows[orig].map((r) => [r.id, sig(r)]))
    const copyIds = new Set(rows[copy].map((r) => r.id))
    const changed: string[] = []
    const added: string[] = []
    rows[copy].forEach((r) => {
      const o = origMap.get(r.id)
      if (o === undefined) added.push(String(r.id))
      else if (o !== sig(r)) changed.push(String(r.id))
    })
    const removed = rows[orig].filter((r) => !copyIds.has(r.id)).map((r) => String(r.id))
    return {
      copyLabel: worldSources[copy].label,
      originalLabel: worldSources[orig].label,
      loaded: reports[copy].loaded && reports[orig].loaded,
      copyRows: rows[copy].length,
      originalRows: rows[orig].length,
      changed,
      added,
      removed,
      identical: changed.length === 0 && added.length === 0 && removed.length === 0,
    }
  })

  // ── Ubicaciones ──
  const locations: MonsterLocation[] = []
  const seenLoc = new Set<string>()
  rows.locations.forEach((r) => {
    const id = str(r.id)
    if (!accept('locations', id, seenLoc)) return
    const mapId = num(r.map_id)
    const monsterId = num(r.monster_id)
    if (mapId === undefined || monsterId === undefined) {
      reports.locations.parseErrors.push(`${id}: map_id o monster_id vacío`)
      return
    }
    locations.push({ id, eventId: str(r.event_id), kind: str(r.kind) ?? 'sin tipo', mapId, monsterId, condition: str(r.presence_condition) })
  })

  // ── Apariciones ──
  const spawns: Spawn[] = []
  const seenSpawn = new Set<string>()
  rows.spawns.forEach((r) => {
    const id = str(r.id)
    if (!accept('spawns', id, seenSpawn)) return
    const mapId = num(r.map_id)
    const monsterId = num(r.monster_id)
    if (mapId === undefined || monsterId === undefined) {
      reports.spawns.parseErrors.push(`${id}: map_id o monster_id vacío`)
      return
    }
    spawns.push({
      id,
      kind: str(r.kind) ?? 'sin tipo',
      mapId,
      monsterId,
      eventId: str(r.event_id),
      mapSelection: str(r.map_selection),
      possibleMaps: numArray(jsonOf<unknown>('spawns', id, 'possible_maps', r.possible_maps)),
      selectionGroup: num(r.selection_group),
      simultaneous: bool(r.simultaneous_in_all_possible_maps),
      quantity: num(r.quantity),
      respawnSeconds: num(r.respawn_seconds),
      xMin: num(r.x_min),
      xMax: num(r.x_max),
      yMin: num(r.y_min),
      yMax: num(r.y_max),
      hp: num(r.hp_effective_config),
      damageMin: num(r.damage_min_effective_config),
      damageMax: num(r.damage_max_effective_config),
      defense: num(r.defense_effective_config),
    })
  })

  // ── Ítems ──
  const items = new Map<string, Item>()
  const seenItems = new Set<string>()
  rows.items.forEach((r) => {
    const id = str(r.id)
    if (!accept('items', id, seenItems)) return
    items.set(id, {
      id,
      name: str(r.name) ?? id,
      section: num(r.section),
      index: num(r.item_index),
      requiredLevel: num(r.required_level_config),
      requiredEnergy: num(r.required_energy_config),
      dropLevel: num(r.drop_level),
      availabilityNote: str(r.availability_note),
      hasVerifiedRoute: bool(r.has_verified_drop_route),
      naturalDropEnabled: bool(r.natural_drop_enabled),
      classCompatible: bool(r.active_class_compatible),
      damageMin: num(r.damage_min),
      damageMax: num(r.damage_max),
      defense: num(r.defense_config),
      attackSpeed: num(r.attack_speed),
      width: num(r.width),
      height: num(r.height),
      slot: num(r.slot),
      skillId: num(r.skill_id),
    })
  })

  // Ítems que el servidor no utiliza: se quitan de toda la guía.
  // Se registra qué se excluye (y por qué) para que el panel separe datos originales de filtros de publicación.
  const rawItemCount = items.size
  const excludedItemIds = new Set<string>()
  const excludedItems: ExcludedItem[] = []
  items.forEach((it, id) => {
    const reason = excludedItemReason(it.name, id)
    if (reason) {
      excludedItemIds.add(id)
      excludedItems.push({ id, name: it.name, reason, routes: 0, targets: 0 })
    }
  })
  excludedItemIds.forEach((id) => items.delete(id))
  const excludedRouteIds = new Set<string>()
  const excludedRouteItem = new Map<string, string>()
  let excludedVariants = 0

  const variants = new Map<string, ItemVariant>()
  const seenVariants = new Set<string>()
  rows.variants.forEach((r) => {
    const id = str(r.id)
    if (!accept('variants', id, seenVariants)) return
    const itemId = str(r.item_id) ?? id
    const name = str(r.name) ?? id
    if (excludedItemIds.has(itemId) || isExcludedItemName(name)) {
      excludedItemIds.add(id)
      excludedVariants++
      return
    }
    // Nombre de presentación por variante, separado del nombre generado por el exportador ("base +nivel").
    const display = variantDisplayName(id)
    variants.set(
      id,
      display
        ? { id, itemId, name: display.displayName, level: num(r.variant_level), exportedName: name !== display.displayName ? name : undefined, nameSource: display.source, nameSourceUrl: display.sourceUrl, nameContext: display.context, aliases: display.aliases }
        : { id, itemId, name, level: num(r.variant_level) },
    )
  })

  const routes: DropRoute[] = []
  const seenRoutes = new Set<string>()
  rows.routes.forEach((r) => {
    const id = str(r.id)
    if (!accept('routes', id, seenRoutes)) return
    const routeItem = str(r.item_id)
    const routeVariant = str(r.variant_id)
    if ((routeItem && excludedItemIds.has(routeItem)) || (routeVariant && excludedItemIds.has(routeVariant))) {
      excludedRouteIds.add(id)
      const baseId = routeItem ?? routeVariant?.split('@')[0]
      if (baseId) excludedRouteItem.set(id, baseId)
      const ex = excludedItems.find((e) => e.id === baseId)
      if (ex) ex.routes++
      return
    }
    const via = jsonOf<unknown>('routes', id, 'via_item_ids', r.via_item_ids)
    routes.push({
      id,
      kind: str(r.kind) ?? 'sin tipo',
      itemId: str(r.item_id),
      variantId: str(r.variant_id),
      description: str(r.description),
      conditions: str(r.conditions),
      probabilityDescription: str(r.probability_description),
      quality: str(r.quality),
      ruleId: str(r.rule_id),
      bagId: str(r.bag_id),
      eventDirectItemId: str(r.event_direct_item_id),
      questDropRuleId: str(r.quest_drop_rule_id),
      runtimeTested: bool(r.runtime_tested),
      verifiedPossible: bool(r.verified_possible),
      viaItemIds: Array.isArray(via) ? via.map(String) : [],
    })
  })

  const rules = new Map<string, DropRule>()
  const seenRules = new Set<string>()
  rows.rules.forEach((r) => {
    const id = str(r.id)
    if (!accept('rules', id, seenRules)) return
    rules.set(id, {
      id,
      itemId: str(r.item_id),
      baseLevel: num(r.base_level),
      grade: num(r.grade),
      mapFilter: num(r.map_filter),
      monsterFilter: num(r.monster_filter),
      monsterLevelMin: num(r.monster_level_min),
      monsterLevelMax: num(r.monster_level_max),
      probabilitySemantics: str(r.probability_semantics),
      rates: [num(r.rate_al0), num(r.rate_al1), num(r.rate_al2), num(r.rate_al3)],
      rateScale: num(r.rate_scale),
    })
  })

  const ruleTargets: RuleTarget[] = []
  const seenTargets = new Set<string>()
  rows.ruleTargets.forEach((r) => {
    const id = str(r.id)
    if (!accept('ruleTargets', id, seenTargets)) return
    ruleTargets.push({ id, ruleId: str(r.rule_id) ?? '', mapId: num(r.map_id), monsterId: num(r.monster_id) })
  })

  const routeTargets: RouteTarget[] = []
  const seenRouteTargets = new Set<string>()
  let excludedRouteTargets = 0
  rows.routeTargets.forEach((r) => {
    const id = str(r.id)
    if (!accept('routeTargets', id, seenRouteTargets)) return
    const routeId = str(r.route_id)
    if (!routeId) {
      if (reports.routeTargets.parseErrors.length < 80) reports.routeTargets.parseErrors.push(`${id}: route_id vacío`)
      return
    }
    if (excludedRouteIds.has(routeId)) {
      excludedRouteTargets++
      const ex = excludedItems.find((e) => e.id === excludedRouteItem.get(routeId))
      if (ex) ex.targets++
      return
    }
    routeTargets.push({
      id,
      routeId,
      monsterId: num(r.monster_id),
      mapId: num(r.map_id),
      eventId: str(r.event_id),
      isIndirect: bool(r.is_indirect),
      locationKind: str(r.location_kind),
    })
  })

  const eventItems: EventDirectItem[] = []
  const seenEvent = new Set<string>()
  rows.eventItems.forEach((r) => {
    const id = str(r.id)
    if (!accept('eventItems', id, seenEvent)) return
    const chance = jsonOf<unknown>('eventItems', id, 'chance_numerator_by_server', r.chance_numerator_by_server)
    eventItems.push({
      id,
      active: bool(r.active),
      eventId: str(r.event_id),
      itemId: str(r.item_id),
      itemLevel: num(r.item_level),
      mapId: num(r.map_id),
      monsterId: num(r.monster_id),
      condition: str(r.condition),
      chanceDenominator: num(r.chance_denominator),
      chanceByServer: chance && typeof chance === 'object' && !Array.isArray(chance) ? (chance as Record<string, number>) : undefined,
      quantityPerRecipient: num(r.quantity_per_recipient),
      quantityPerSuccess: num(r.quantity_per_success),
      roomNumber: num(r.room_number),
      weekday: num(r.weekday),
    })
  })

  const rates: ServerRate[] = []
  const seenRates = new Set<string>()
  rows.rates.forEach((r) => {
    const id = str(r.id)
    if (!accept('rates', id, seenRates)) return
    rates.push({ id, accountName: str(r.account_name), accountLevel: num(r.account_level), itemDropRate: num(r.item_drop_rate), monsterLifeRate: num(r.monster_life_rate), scope: str(r.scope), serverRole: str(r.server_role) })
  })

  const rulesList = Array.from(rules.values())
  const w0Variants = variants

  // ── Equivalencias de eventos (comprobadas, nunca supuestas) ──
  const eventLinks = new Map<string, EventLink>()
  const allEventIds = new Set<string>(eventEquivalences.map((e) => e.eventId))
  spawns.forEach((s) => s.eventId && allEventIds.add(s.eventId))
  locations.forEach((l) => l.eventId && allEventIds.add(l.eventId))
  allEventIds.forEach((eventId) => {
    const spawnIds = spawns.filter((s) => s.eventId === eventId).map((s) => s.id)
    const slugs = Array.from(new Set(spawnIds.map((id) => /^\d+-invasion-(.+)-\d+$/.exec(id)?.[1]).filter((v): v is string => !!v)))
    const eq = eventEquivalences.find((e) => e.eventId === eventId)
    const base = { eventId, spawnIds, slugs, approvedPerAppearance: eq?.approvedPerAppearance }
    if (!eq) {
      eventLinks.set(eventId, { ...base, status: 'unmapped', evidence: 'Sin equivalencia en la tabla. No se enlaza a ninguna ficha.' })
    } else if (!eq.guideEntryId) {
      eventLinks.set(eventId, { ...base, status: 'pending', evidence: eq.note ?? 'Sin ficha aprobada.' })
    } else if (slugs.length > 0 && slugs.length === new Set(spawnIds.map((id) => /^\d+-invasion-(.+)-\d+$/.exec(id)?.[1])).size && slugs.every((s) => s === eq.spawnSlug)) {
      eventLinks.set(eventId, { ...base, guideEntryId: eq.guideEntryId, status: 'verified', evidence: `Los ${spawnIds.length} registros de spawns.csv con ${eventId} se llaman "*-invasion-${eq.spawnSlug}-*".` })
    } else {
      eventLinks.set(eventId, { ...base, status: 'mismatch', evidence: `Se esperaba "${eq.spawnSlug}" y se encontró: ${slugs.join(', ') || 'ningún registro'}. No se enlaza.` })
    }
  })

  // ── Clases (04, 05) ──
  const classNames = new Map<string, string>()
  const activeClassIds = new Set<string>()
  const seenClasses = new Set<string>()
  rows.classes.forEach((r) => {
    const id = str(r.id)
    if (!accept('classes', id, seenClasses)) return
    classNames.set(id, str(r.name) ?? id)
    if (bool(r.active) === true) activeClassIds.add(id)
  })
  const itemClasses = new Map<string, string[]>()
  const seenItemClasses = new Set<string>()
  rows.itemClasses.forEach((r) => {
    const id = str(r.id)
    if (!accept('itemClasses', id, seenItemClasses)) return
    const itemId = str(r.item_id)
    const classId = str(r.class_id)
    if (!itemId || classId === undefined) return
    const arr = itemClasses.get(itemId) ?? []
    arr.push(classId)
    itemClasses.set(itemId, arr)
  })
  // 03_categories y 14_route_events se cargan para el panel (conteos y errores); no cambian la ficha del jugador.

  // ── Bolsas de recompensa (16–22) ──
  const bags = new Map<string, Bag>()
  const seenBags = new Set<string>()
  rows.bags.forEach((r) => {
    const id = str(r.id)
    if (!accept('bags', id, seenBags)) return
    bags.set(id, { id, name: str(r.name), deliveryMode: str(r.delivery_mode), usedByRuntime: bool(r.used_by_runtime), registered: bool(r.registered), contentExists: bool(r.content_exists) })
  })
  const bagTriggers: BagTrigger[] = []
  const seenTriggers = new Set<string>()
  rows.bagTriggers.forEach((r) => {
    const id = str(r.id)
    const bagId = str(r.bag_id)
    if (!accept('bagTriggers', id, seenTriggers) || bagId === undefined) return
    bagTriggers.push({ id, bagId, itemId: str(r.item_id), itemLevel: num(r.item_level), monsterId: num(r.monster_id), deliveryMode: str(r.delivery_mode), usedByRuntime: bool(r.used_by_runtime) })
  })
  const bagSections: BagSection[] = []
  const seenSections = new Set<string>()
  rows.bagSections.forEach((r) => {
    const id = str(r.id)
    const bagId = str(r.bag_id)
    if (!accept('bagSections', id, seenSections) || bagId === undefined) return
    bagSections.push({ id, bagId, poolId: str(r.reward_pool_id), giveAll: bool(r.give_all), weight: num(r.weight), rewardKind: str(r.reward_kind), usedByRuntime: bool(r.used_by_runtime) })
  })
  const bagRewardPools = new Map<string, BagRewardPool>()
  const seenPools = new Set<string>()
  rows.bagRewardPools.forEach((r) => {
    const id = str(r.id)
    if (!accept('bagRewardPools', id, seenPools)) return
    bagRewardPools.set(id, { id, bagId: str(r.bag_id), acceptedRows: num(r.accepted_row_count), uniformPercent: num(r.uniform_row_selection_percent), selectionScope: str(r.selection_scope) })
  })
  const bagRewards: BagReward[] = []
  const seenRewards = new Set<string>()
  rows.bagRewards.forEach((r) => {
    const id = str(r.id)
    const bagId = str(r.bag_id)
    if (!accept('bagRewards', id, seenRewards) || bagId === undefined) return
    const itemId = str(r.item_id)
    if (itemId && excludedItemIds.has(itemId)) return
    bagRewards.push({ id, bagId, poolId: str(r.pool_id), itemId, variantId: str(r.variant_id), usedByRuntime: bool(r.used_by_runtime), catalogExists: bool(r.catalog_exists) })
  })
  const bagRewardVariants = new Map<string, BagRewardVariant>()
  const seenRV = new Set<string>()
  rows.bagRewardVariants.forEach((r) => {
    const id = str(r.id)
    if (!accept('bagRewardVariants', id, seenRV)) return
    bagRewardVariants.set(id, { id, levelMin: num(r.effective_level_min) ?? num(r.level_min), levelMax: num(r.effective_level_max) ?? num(r.level_max) })
  })

  // ── Eventos (27, 28, 30, 33, 34) ──
  const events = new Map<string, GameEvent>()
  const seenEvents = new Set<string>()
  rows.events.forEach((r) => {
    const id = str(r.id)
    if (!accept('events', id, seenEvents)) return
    events.set(id, {
      id,
      name: str(r.name) ?? id,
      type: str(r.type),
      activeConfigured: bool(r.active_configured),
      enabledByServer: str(r.enabled_by_server),
      serverFlags: parseServerFlags(str(r.enabled_by_server)),
      invasionIndex: num(r.invasion_index),
      dailyOccurrences: num(r.daily_occurrences),
      durationSeconds: num(r.duration_seconds),
      mapSelection: str(r.map_selection),
      possibleMapIds: numArray(jsonOf<unknown>('events', id, 'possible_map_ids', r.possible_map_ids)),
      timingType: str(r.timing_type),
      hasAutomaticSchedule: bool(r.has_automatic_schedule),
      bossMonsterId: num(r.boss_monster_id),
      notes: str(r.notes),
    })
  })
  const eventSchedules: EventSchedule[] = []
  const seenSched = new Set<string>()
  rows.eventSchedules.forEach((r) => {
    const id = str(r.id)
    const eventId = str(r.event_id)
    if (!accept('eventSchedules', id, seenSched) || eventId === undefined) return
    eventSchedules.push({ id, eventId, hour: num(r.hour), minute: num(r.minute), weekday: num(r.weekday), day: num(r.day), recurrence: str(r.recurrence), state: str(r.state), semantic: str(r.semantic) })
  })
  const eventMapChoices: EventMapChoice[] = []
  const seenChoices = new Set<string>()
  rows.eventMapChoices.forEach((r) => {
    const id = str(r.id)
    const eventId = str(r.event_id)
    if (!accept('eventMapChoices', id, seenChoices) || eventId === undefined) return
    eventMapChoices.push({ id, eventId, mapId: num(r.map_id), probability: num(r.probability_given_group) })
  })
  const eventRooms: EventRoom[] = []
  const seenRooms = new Set<string>()
  rows.eventRooms.forEach((r) => {
    const id = str(r.id)
    const eventId = str(r.event_id)
    if (!accept('eventRooms', id, seenRooms) || eventId === undefined) return
    eventRooms.push({
      id, eventId, roomNumber: num(r.room_number), levelMin: num(r.level_min), levelMax: num(r.level_max), resetMin: num(r.reset_min), resetMax: num(r.reset_max),
      mapId: num(r.map_id), active: bool(r.active), entryEnabled: bool(r.entry_enabled), masterRequired: bool(r.master_character_required),
      validLevelRange: bool(r.valid_level_range), serverFlags: parseServerFlags(str(r.available_servers)),
    })
  })
  const eventBags: EventBag[] = []
  const seenEB = new Set<string>()
  rows.eventBags.forEach((r) => {
    const id = str(r.id)
    const eventId = str(r.event_id)
    const bagId = str(r.bag_id)
    if (!accept('eventBags', id, seenEB) || eventId === undefined || bagId === undefined) return
    eventBags.push({ id, eventId, bagId, monsterId: num(r.monster_id), mapId: num(r.map_id), rewardTier: str(r.reward_tier), roomNumber: num(r.room_number), awardCondition: str(r.award_condition), active: bool(r.active) })
  })

  const quests = parseQuests(
    {
      quests: rows.quests,
      questRules: rows.questRules,
      questRuleClasses: rows.questRuleClasses,
      questObjectives: rows.questObjectives,
      questObjectiveClasses: rows.questObjectiveClasses,
      questDropRules: rows.questDropRules,
      questDropRuleTargets: rows.questDropRuleTargets,
    },
    accept,
  )

  return {
    quests,
    bags,
    bagTriggers,
    bagSections,
    bagRewardPools,
    bagRewards,
    bagRewardVariants,
    events,
    eventSchedules,
    eventRooms,
    eventBags,
    eventMapChoices,
    classNames,
    activeClassIds,
    itemClasses,
    eventLinks,
    maps,
    monsters,
    locations,
    spawns,
    spawnById: new Map(spawns.map((s) => [s.id, s])),
    items,
    variants,
    routes,
    rules,
    ruleTargets,
    routeTargets,
    eventItems,
    rates,
    idx: {
      spawnsByMonster: groupBy(spawns, (s) => s.monsterId),
      spawnsByMap: groupBy(spawns, (s) => s.mapId),
      locationsByMonster: groupBy(locations, (l) => l.monsterId),
      locationsByMap: groupBy(locations, (l) => l.mapId),
      variantsByItem: groupBy(Array.from(variants.values()), (v) => v.itemId),
      routesByItem: groupBy(routes, (r) => r.itemId),
      routesByVariant: groupBy(routes, (r) => r.variantId),
      rulesByMonster: groupBy(rulesList, (r) => r.monsterFilter),
      targetsByRule: groupBy(ruleTargets, (t) => t.ruleId),
      targetsByMonster: groupBy(ruleTargets, (t) => t.monsterId),
      routeTargetsByRoute: groupBy(routeTargets, (t) => t.routeId),
      routeTargetsByMonster: groupBy(routeTargets, (t) => t.monsterId),
      routeById: new Map(routes.map((r) => [r.id, r])),
      eventItemsByItem: groupBy(eventItems, (e) => e.itemId),
      eventItemsByMonster: groupBy(eventItems, (e) => e.monsterId),
      eventItemById: new Map(eventItems.map((e) => [e.id, e])),
      bagRewardsByItem: (() => {
        const m = groupBy(bagRewards, (b) => b.itemId)
        bagRewards.forEach((b) => {
          if (b.variantId && w0Variants.has(b.variantId) && b.variantId !== b.itemId) {
            const arr = m.get(b.variantId) ?? []
            arr.push(b)
            m.set(b.variantId, arr)
          }
        })
        return m
      })(),
      bagRewardsByPool: groupBy(bagRewards, (b) => b.poolId),
      bagTriggersByBag: groupBy(bagTriggers, (t) => t.bagId),
      bagSectionsByPool: groupBy(bagSections, (s) => s.poolId),
      eventBagsByBag: groupBy(eventBags, (e) => e.bagId),
      eventBagsByEvent: groupBy(eventBags, (e) => e.eventId),
      schedulesByEvent: groupBy(eventSchedules, (s) => s.eventId),
      roomsByEvent: groupBy(eventRooms, (r) => r.eventId),
    },
    reports: keys.map((k) => reports[k]),
    publication: {
      rawItems: rawItemCount,
      excludedItems,
      excludedVariants,
      excludedRoutes: excludedRouteIds.size,
      excludedRouteTargets,
    },
    monstersCopy,
    copies,
  }
}
